import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Search,
  Flame,
  TrendingUp,
  TrendingDown,
  Minus,
  Radar,
  Plus,
  Loader2,
  Power,
  PowerOff,
  AlertCircle,
  User,
  RefreshCw,
  X,
  ChevronDown,
  Eye,
  Lock,
  Zap,
  Target,
  Check,
  DollarSign,
  BarChart3,
  Instagram,
  Music,
  Facebook
} from 'lucide-react'
import { Card, KPICard } from '@/components/ui'
import { DateRangePicker } from '@/components/ui/DateRangePicker'
import { useNavigationTracking } from '@/contexts/AnalyticsContext'
import { useToast } from '@/contexts/ToastContext'
import useScoutStore from '@/stores/scoutStore'
import useGlobalCustomerStore from '@/stores/globalCustomerStore'
import { useAuthStore } from '@/stores/authStore'
import usePermissionsStore from '@/stores/permissionsStore'
import AddAccountDrawer from './AddAccountDrawer'

type ScoutTab = 'brands' | 'accounts'

// Heat category badge styling
const heatBadgeStyles: Record<string, string> = {
  super_hot: 'bg-orange-200 text-orange-800',
  hot: 'bg-red-100 text-red-700',
  warm: 'bg-amber-100 text-amber-700',
  cold: 'bg-blue-100 text-blue-700'
}

const heatBadgeLabel: Record<string, string> = {
  super_hot: 'Super Hot',
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold'
}

// Card tint by heat category — subtle gradient + border in our light scheme.
const heatCardStyles: Record<string, string> = {
  super_hot: 'from-white to-orange-50 border-orange-200 hover:border-orange-300',
  hot: 'from-white to-red-50 border-red-200 hover:border-red-300',
  warm: 'from-white to-amber-50 border-amber-200 hover:border-amber-300',
  cold: 'from-white to-blue-50 border-blue-200 hover:border-blue-300'
}

// Small trend indicator (icon + color) for the card header.
const trendIconFor = (trend: string): { Icon: typeof TrendingUp; color: string } => {
  if (trend === 'rising') return { Icon: TrendingUp, color: 'text-emerald-600' }
  if (trend === 'falling' || trend === 'declining') return { Icon: TrendingDown, color: 'text-red-500' }
  return { Icon: Minus, color: 'text-gray-400' }
}

const formatEMV = (value: number | string): string => {
  const n = Number(value)
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`
  if (n >= 1000) return `$${(n / 1000).toFixed(2)}K`
  return `$${n.toFixed(2)}`
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const acctPlatformIcon = (id: number) => (id === 2 ? Music : id === 11 ? Facebook : Instagram)
const acctPlatformColor = (id: number) => (id === 2 ? 'text-black' : id === 11 ? 'text-blue-600' : 'text-pink-500')

const BrandScoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { trackTabChanged } = useNavigationTracking()
  const { showSuccess, showError } = useToast()
  const { selectedCustomer } = useGlobalCustomerStore()
  const { user } = useAuthStore()
  const { hasFeature, loadPermissions, permissions } = usePermissionsStore()
  const {
    brands,
    summary,
    pagination,
    loading,
    error,
    loadBrands,
    accounts,
    accountsLoading,
    accountsError,
    loadAccounts,
    brandCategories,
    loadBrandCategories,
    updateAccount,
    reset
  } = useScoutStore()

  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<ScoutTab>('brands')
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '')
  const [showAddDrawer, setShowAddDrawer] = useState(false)
  const [togglingAccountId, setTogglingAccountId] = useState<string | null>(null)
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false)
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false)
  const [hoveredBrandId, setHoveredBrandId] = useState<string | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const accountDropdownRef = useRef<HTMLDivElement>(null)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // All filter state lives in the URL so it survives navigation
  const currentPage = parseInt(searchParams.get('page') || '1', 10)
  const sortBy = searchParams.get('sortBy') || 'total_emv'
  const sortOrder = searchParams.get('sortOrder') || 'desc'
  const period = searchParams.get('period') || '90days'
  const selectedAccountIds = useMemo(
    () => searchParams.get('accountIds')?.split(',').filter(Boolean) ?? [],
    [searchParams]
  )
  const selectedCategories = useMemo(
    () => searchParams.get('categories')?.split(',').filter(Boolean) ?? [],
    [searchParams]
  )
  const customDateRange = useMemo(() => {
    const start = searchParams.get('customStartDate')
    const end = searchParams.get('customEndDate')
    return start && end ? { start, end } : null
  }, [searchParams])

  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    const next = typeof page === 'function' ? page(currentPage) : page
    setSearchParams(prev => { prev.set('page', String(next)); return prev }, { replace: true })
  }

  const customerId = selectedCustomer?.id ? String(selectedCustomer.id) : ''

  // Load org-level permissions. Force a refresh: the store is persisted to localStorage,
  // so without this the gate can read a stale `brand_scout: true` that survives a page
  // reload — a revoked feature would stay accessible until the cache happened to lapse.
  // (SocialMediaDashboard already forces for the same reason.)
  useEffect(() => {
    if (user?.organizationId) {
      loadPermissions(user.organizationId, true)
    }
  }, [user?.organizationId, loadPermissions])

  // Gate on org feature: only block when permissions have loaded and brand_scout is explicitly false
  const permissionsLoaded = !!permissions
  const isFeatureNotEnabled = (permissionsLoaded && !hasFeature('brand_scout')) || error?.toLowerCase().includes('not enabled')

  // Show toast on error — but not for feature-gated errors (handled inline)
  useEffect(() => {
    if (error && !error.toLowerCase().includes('not enabled')) {
      showError('Unable to load brands')
    }
  }, [error, showError])

  useEffect(() => {
    if (accountsError && activeTab === 'accounts') {
      showError('Unable to load accounts')
    }
  }, [accountsError, activeTab, showError])

  // Close account dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target as Node)) {
        setAccountDropdownOpen(false)
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setCategoryDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Load accounts and categories for filter dropdowns when on brands tab
  useEffect(() => {
    if (customerId && activeTab === 'brands' && !isFeatureNotEnabled) {
      if (accounts.length === 0 && !accountsLoading && !accountsError) {
        loadAccounts(customerId)
      }
      if (brandCategories.length === 0) {
        loadBrandCategories(customerId)
      }
    }
  }, [customerId, activeTab, accounts.length, accountsLoading, accountsError, brandCategories.length, isFeatureNotEnabled, loadAccounts, loadBrandCategories])

  // Load data when switching tabs or when query params change
  const loadBrandsData = useCallback(() => {
    if (!customerId) return
    loadBrands(
      customerId, currentPage, 32, sortBy, sortOrder,
      searchParams.get('search') || '',
      selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
      period,
      period === 'custom' && customDateRange ? customDateRange.start : undefined,
      period === 'custom' && customDateRange ? customDateRange.end : undefined,
      selectedCategories.length > 0 ? selectedCategories : undefined
    )
  }, [customerId, currentPage, sortBy, sortOrder, searchParams, selectedAccountIds, selectedCategories, period, customDateRange, loadBrands])

  useEffect(() => {
    if (activeTab === 'brands') {
      loadBrandsData()
    }
  }, [activeTab, loadBrandsData])

  useEffect(() => {
    if (activeTab === 'accounts' && customerId) {
      loadAccounts(customerId)
    }
  }, [activeTab, customerId, loadAccounts])

  // Auto-refresh the account list while any scan is in progress, so the status badge
  // transitions out of "Scanning" to its terminal state (completed/failed/partial)
  // on its own — otherwise a scan that ends (or fails) leaves the account stuck showing
  // "Scanning" until the user manually reloads.
  const anyAccountScanning = useMemo(
    () =>
      accounts.some(
        (a) =>
          a.last_scan_status &&
          ['pending', 'scraping', 'analyzing', 'scoring'].includes(a.last_scan_status)
      ),
    [accounts]
  )

  useEffect(() => {
    if (activeTab !== 'accounts' || !customerId || !anyAccountScanning) return
    const interval = setInterval(() => {
      loadAccounts(customerId)
    }, 15000)
    return () => clearInterval(interval)
  }, [activeTab, customerId, anyAccountScanning, loadAccounts])

  // Reset on unmount
  useEffect(() => {
    return () => reset()
  }, [customerId, reset])

  const handleTabChange = (tab: ScoutTab) => {
    trackTabChanged(tab, activeTab)
    setActiveTab(tab)
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      setSearchParams(prev => {
        if (value) prev.set('search', value)
        else prev.delete('search')
        prev.set('page', '1')
        return prev
      }, { replace: true })
    }, 400)
  }

  const handleClearSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    setSearchQuery('')
    setSearchParams(prev => { prev.delete('search'); prev.set('page', '1'); return prev }, { replace: true })
  }

  const handleAccountToggle = (accountId: string) => {
    const next = selectedAccountIds.includes(accountId)
      ? selectedAccountIds.filter(id => id !== accountId)
      : [...selectedAccountIds, accountId]
    setSearchParams(prev => {
      if (next.length > 0) prev.set('accountIds', next.join(','))
      else prev.delete('accountIds')
      prev.set('page', '1')
      return prev
    }, { replace: true })
  }

  const handleClearAccounts = () => {
    setSearchParams(prev => { prev.delete('accountIds'); prev.set('page', '1'); return prev }, { replace: true })
  }

  const handleCategoryToggle = (category: string) => {
    const next = selectedCategories.includes(category)
      ? selectedCategories.filter(c => c !== category)
      : [...selectedCategories, category]
    setSearchParams(prev => {
      if (next.length > 0) prev.set('categories', next.join(','))
      else prev.delete('categories')
      prev.set('page', '1')
      return prev
    }, { replace: true })
  }

  const handleClearCategories = () => {
    setSearchParams(prev => { prev.delete('categories'); prev.set('page', '1'); return prev }, { replace: true })
  }

  const handleDateRangeChange = (range: { start: string; end: string } | null, newPeriod: string) => {
    setSearchParams(prev => {
      prev.set('period', newPeriod)
      prev.set('page', '1')
      if (newPeriod === 'custom' && range) {
        prev.set('customStartDate', range.start)
        prev.set('customEndDate', range.end)
      } else {
        prev.delete('customStartDate')
        prev.delete('customEndDate')
      }
      return prev
    }, { replace: true })
  }

  const handleRowMouseEnter = (e: React.MouseEvent<HTMLElement>, brandId: string) => {
    const firstCell = (e.currentTarget as HTMLTableRowElement).cells[0]
    const rect = firstCell.getBoundingClientRect()
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)
    tooltipTimerRef.current = setTimeout(() => {
      setHoveredBrandId(brandId)
      setTooltipPos({ x: rect.right + 8, y: rect.top + rect.height / 2 })
    }, 800)
  }

  const handleBrandClick = (brandId: string) => {
    const params = new URLSearchParams()
    if (selectedAccountIds.length > 0) params.set('accountIds', selectedAccountIds.join(','))
    if (period) params.set('period', period)
    if (period === 'custom' && customDateRange) {
      params.set('customStartDate', customDateRange.start)
      params.set('customEndDate', customDateRange.end)
    }
    const qs = params.toString()
    navigate(`/brand-scout/${brandId}${qs ? `?${qs}` : ''}`)
  }

  const handleToggleAccount = async (accountId: string, currentActive: boolean) => {
    if (!customerId) return
    setTogglingAccountId(accountId)
    try {
      await updateAccount(accountId, customerId, { is_active: !currentActive })
      await loadAccounts(customerId)
      showSuccess('Account Updated', `Account ${currentActive ? 'disabled' : 'enabled'}`)
    } catch (err) {
      showError('Update Failed', err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setTogglingAccountId(null)
    }
  }

  if (!selectedCustomer) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
        <p className="text-gray-500 text-sm">Please select a customer to view Brand Scout.</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Radar className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Brand Scout</h1>
              <p className="text-sm text-gray-600">Discover the brands partnering with your peers and competition</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner mb-6">
          <button
            onClick={() => handleTabChange('brands')}
            className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
              activeTab === 'brands'
                ? 'bg-white text-blue-600 shadow-md border border-white/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <Flame className="h-3.5 w-3.5 mr-1.5" />
            Brands
          </button>
          <button
            onClick={() => !isFeatureNotEnabled && handleTabChange('accounts')}
            disabled={isFeatureNotEnabled}
            className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
              isFeatureNotEnabled
                ? 'text-gray-400 cursor-not-allowed opacity-50'
                : activeTab === 'accounts'
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <User className="h-3.5 w-3.5 mr-1.5" />
            Accounts
          </button>
        </div>

        {/* Brands Tab */}
        {activeTab === 'brands' && (
          <div className="space-y-4">
            {/* KPI cards — reflect the currently-applied filters (re-queried on filter change).
                Matches the Social Dashboard KPI pattern: gradient card, hover shadow, and an
                icon badge that scales on hover (group-hover). */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard label="Total EMV" value={formatEMV(summary?.total_emv ?? 0)} icon={DollarSign} colorScheme="emerald" loading={loading} />
              <KPICard label="Total Posts" value={(summary?.total_posts ?? 0).toLocaleString()} icon={BarChart3} colorScheme="purple" loading={loading} />
              <KPICard label="Avg EMV" value={formatEMV(summary?.avg_emv ?? 0)} icon={TrendingUp} colorScheme="amber" loading={loading} />
              <KPICard label="Unique Brands" value={(summary?.unique_brands ?? summary?.total_brands ?? 0).toLocaleString()} icon={Radar} colorScheme="blue" loading={loading} />
            </div>

            {/* Filter Bar */}
            <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search brands..."
                  disabled={isFeatureNotEnabled}
                  className={`pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-72 ${
                    isFeatureNotEnabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                  }`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>

              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={loadBrandsData}
                  disabled={loading}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                </button>

                {/* Account Multi-Select Dropdown */}
                <div className="relative" ref={accountDropdownRef}>
                  <button
                    onClick={() => !isFeatureNotEnabled && setAccountDropdownOpen(!accountDropdownOpen)}
                    disabled={isFeatureNotEnabled}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg transition-colors shadow-sm ${
                      isFeatureNotEnabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <User className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                    {selectedAccountIds.length === 0
                      ? 'All Accounts'
                      : `${selectedAccountIds.length} account${selectedAccountIds.length > 1 ? 's' : ''}`}
                    <ChevronDown className="h-3 w-3 ml-1.5 text-gray-400" />
                  </button>

                  {accountDropdownOpen && (
                    <div className="absolute top-full right-0 mt-1 z-50 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                      {selectedAccountIds.length > 0 && (
                        <button
                          onClick={handleClearAccounts}
                          className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 transition-colors border-b border-gray-100"
                        >
                          Clear selection
                        </button>
                      )}
                      {accounts.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-500">No accounts found</div>
                      ) : (
                        [...accounts]
                          .sort((a, b) => a.username.localeCompare(b.username, undefined, { sensitivity: 'base' }))
                          .map((account) => (
                          <button
                            key={account.id}
                            onClick={() => handleAccountToggle(account.id)}
                            className="w-full flex items-center px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                          >
                            <div className={`h-3.5 w-3.5 mr-2 rounded border flex items-center justify-center flex-shrink-0 ${
                              selectedAccountIds.includes(account.id)
                                ? 'bg-blue-600 border-blue-600'
                                : 'border-gray-300'
                            }`}>
                              {selectedAccountIds.includes(account.id) && (
                                <Check className="h-2.5 w-2.5 text-white" />
                              )}
                            </div>
                            {(() => { const Icon = acctPlatformIcon(account.platform_id); return <Icon className={`h-3.5 w-3.5 mr-1.5 flex-shrink-0 ${acctPlatformColor(account.platform_id)}`} /> })()}
                            <span className="text-gray-700 truncate">@{account.username}</span>
                          </button>
                        )))}
                    </div>
                  )}
                </div>

                {/* Category Multi-Select Dropdown */}
                <div className="relative" ref={categoryDropdownRef}>
                  <button
                    onClick={() => !isFeatureNotEnabled && setCategoryDropdownOpen(!categoryDropdownOpen)}
                    disabled={isFeatureNotEnabled}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg transition-colors shadow-sm ${
                      isFeatureNotEnabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <Target className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                    {selectedCategories.length === 0
                      ? 'All Categories'
                      : `${selectedCategories.length} categor${selectedCategories.length > 1 ? 'ies' : 'y'}`}
                    <ChevronDown className="h-3 w-3 ml-1.5 text-gray-400" />
                  </button>

                  {categoryDropdownOpen && (
                    <div className="absolute top-full right-0 mt-1 z-50 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                      {selectedCategories.length > 0 && (
                        <button
                          onClick={handleClearCategories}
                          className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 transition-colors border-b border-gray-100"
                        >
                          Clear selection
                        </button>
                      )}
                      {brandCategories.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-500">No categories found</div>
                      ) : (
                        brandCategories.map((category) => (
                          <button
                            key={category}
                            onClick={() => handleCategoryToggle(category)}
                            className="w-full flex items-center px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                          >
                            <div className={`h-3.5 w-3.5 mr-2 rounded border flex items-center justify-center flex-shrink-0 ${
                              selectedCategories.includes(category)
                                ? 'bg-blue-600 border-blue-600'
                                : 'border-gray-300'
                            }`}>
                              {selectedCategories.includes(category) && (
                                <Check className="h-2.5 w-2.5 text-white" />
                              )}
                            </div>
                            <span className="text-gray-700 truncate">{category}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Date Range Picker */}
                <DateRangePicker
                  value={customDateRange}
                  onChange={handleDateRangeChange}
                  maxRetentionDays={365}
                  selectedPeriod={period}
                  disabled={isFeatureNotEnabled}
                />
              </div>
            </div>

            {/* Brands Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : isFeatureNotEnabled ? (
                <div className="flex items-center justify-center py-12">
                  <div className="max-w-md w-full">
                    <Card className="p-6 text-center">
                      <div className="flex justify-center mb-4">
                        <div className="p-3 bg-gray-100 rounded-full">
                          <Lock className="h-10 w-10 text-gray-400" />
                        </div>
                      </div>
                      <h2 className="text-sm font-bold text-gray-900 mb-2">
                        Brand Scout is not enabled for your organization
                      </h2>
                      <p className="text-xs text-gray-600 mb-4">
                        Brand Scout is not available in your current subscription tier.
                        Upgrade your plan to unlock AI-powered brand detection across peer and partner content.
                      </p>
                      <div className="space-y-2 text-left mb-6">
                        <h3 className="text-xs font-semibold text-gray-900">With Brand Scout, you can:</h3>
                        <ul className="space-y-1.5 text-xs text-gray-600">
                          <li className="flex items-start">
                            <Eye className="h-3.5 w-3.5 text-indigo-500 mr-2 mt-0.5" />
                            <span>Automatically detect brands appearing in peer and partner content</span>
                          </li>
                          <li className="flex items-start">
                            <Radar className="h-3.5 w-3.5 text-indigo-500 mr-2 mt-0.5" />
                            <span>Track brand presence with heat scores and trend analysis</span>
                          </li>
                          <li className="flex items-start">
                            <Target className="h-3.5 w-3.5 text-indigo-500 mr-2 mt-0.5" />
                            <span>Discover new sponsorship opportunities from organic mentions</span>
                          </li>
                          <li className="flex items-start">
                            <Zap className="h-3.5 w-3.5 text-indigo-500 mr-2 mt-0.5" />
                            <span>Monitor multiple accounts with scheduled scanning</span>
                          </li>
                        </ul>
                      </div>
                      {/* TODO: Upgrade button to be implemented at a later time */}
                      <p className="text-xs text-gray-500 mt-3">
                        Contact AVI Support for upgrade options
                      </p>
                    </Card>
                  </div>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Unable to load brands</h3>
                  <p className="text-xs text-gray-500 mb-4 max-w-sm">{error}</p>
                  <button
                    onClick={loadBrandsData}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Try Again
                  </button>
                </div>
              ) : brands.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Radar className="h-6 w-6 text-gray-400" />
                  </div>
                  {searchQuery || selectedAccountIds.length > 0 || selectedCategories.length > 0 || period !== '90days' ? (
                    <>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">No brands found</h3>
                      <p className="text-xs text-gray-500 mb-4">Try adjusting your filters</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">No brands detected yet</h3>
                      <p className="text-xs text-gray-500 mb-4">Add accounts in the Accounts tab to start scanning</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {brands.map((brand) => {
                    const { Icon: TrendIco, color: trendColor } = trendIconFor(brand.heat_trend)
                    const heatScore = +Number(brand.heat_score).toFixed(1)
                    return (
                      <div
                        key={brand.id}
                        onClick={() => handleBrandClick(brand.id)}
                        onMouseEnter={(e) => handleRowMouseEnter(e, brand.id)}
                        onMouseLeave={() => {
                          if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)
                          setHoveredBrandId(null)
                        }}
                        className={`cursor-pointer rounded-xl border bg-gradient-to-br p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 ${heatCardStyles[brand.heat_category] || heatCardStyles.cold}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 truncate">{brand.brand_name}</h3>
                            <div className="text-[10px] text-gray-500 truncate">{brand.category || 'Uncategorized'}</div>
                          </div>
                          <TrendIco className={`h-4 w-4 flex-shrink-0 ${trendColor}`} />
                        </div>
                        <div className="mb-2">
                          <div className="text-2xl font-bold text-gray-900 leading-tight">{formatEMV(brand.total_emv ?? 0)}</div>
                          <div className="text-[11px] text-gray-500">total EMV</div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                          <span>{formatEMV(brand.avg_emv ?? 0)} avg</span>
                          <span>{brand.filtered_posts ?? brand.total_posts ?? 0} posts</span>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${heatBadgeStyles[brand.heat_category] || heatBadgeStyles.cold}`}>
                          {heatBadgeLabel[brand.heat_category] || 'Cold'} · {heatScore}
                        </span>
                        <div className="mt-3 pt-2 border-t border-gray-100 text-[10px] text-gray-500">
                          Last seen {formatDate(brand.last_seen_at)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Pagination */}
              {pagination && pagination.totalItems > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 px-3 py-4">
                  <div className="text-xs text-gray-500">
                    Showing {(pagination.currentPage - 1) * 32 + 1}-{Math.min(pagination.currentPage * 32, pagination.totalItems)} of {pagination.totalItems} brands
                  </div>
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={!pagination.hasPrevPage || loading}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        First
                      </button>
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={!pagination.hasPrevPage || loading}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage((p) => p + 1)}
                        disabled={!pagination.hasNextPage || loading}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setCurrentPage(pagination.totalPages)}
                        disabled={!pagination.hasNextPage || loading}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Last
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Brand row hover tooltip */}
        {hoveredBrandId && (() => {
          const hb = brands.find(b => b.id === hoveredBrandId)
          if (!hb?.account_usernames?.length) return null
          return createPortal(
            <div
              style={{
                position: 'fixed',
                left: tooltipPos.x,
                top: tooltipPos.y,
                zIndex: 9999,
                transform: 'translateY(-50%)',
                maxWidth: 200
              }}
              className="pointer-events-none bg-gray-900 text-white rounded shadow-lg px-2.5 py-2"
            >
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Appears in</p>
              {hb.account_usernames.slice(0, 5).map(u => (
                <p key={u} className="text-xs text-gray-200">@{u}</p>
              ))}
              {hb.account_usernames.length > 5 && (
                <p className="text-[10px] text-gray-400 mt-1">+{hb.account_usernames.length - 5} more</p>
              )}
            </div>,
            document.body
          )
        })()}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">Scout Accounts</h2>
              <button
                onClick={() => setShowAddDrawer(true)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Account
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {accountsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : accountsError ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Unable to load accounts</h3>
                  <p className="text-xs text-gray-500 mb-4 max-w-sm">{accountsError}</p>
                  <button
                    onClick={() => loadAccounts(customerId)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Try Again
                  </button>
                </div>
              ) : accounts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <User className="h-6 w-6 text-gray-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">No accounts added yet</h3>
                  <p className="text-xs text-gray-500 mb-4">Add an Instagram account to start brand scanning</p>
                  <button
                    onClick={() => setShowAddDrawer(true)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Add Account
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Account</th>
                        <th className="text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Successful Scans</th>
                        <th className="text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Scan Frequency</th>
                        <th className="text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Last Scanned</th>
                        <th className="text-center text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Status</th>
                        <th className="text-center text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {accounts.map((account) => (
                        <tr key={account.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2">
                            <div>
                              <span className="text-xs font-medium text-gray-900">@{account.username}</span>
                              {account.display_name && (
                                <p className="text-[10px] text-gray-500">{account.display_name}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className="text-xs text-gray-900">
                              {account.completed_scans}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {/* Hard-coded for now; will become a configurable per-account cadence. */}
                            <span className="text-xs text-gray-900">Weekly</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className="text-xs text-gray-900">
                              {formatDate(account.last_scanned_at)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {(() => {
                              const scanStatus = account.last_scan_status
                              const isScanning = scanStatus && ['pending', 'scraping', 'analyzing', 'scoring'].includes(scanStatus)
                              const isFailed = scanStatus === 'failed'
                              const isPartial = scanStatus === 'partial'
                              const isComplete = scanStatus === 'completed'

                              if (isScanning) {
                                return (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Scanning
                                  </span>
                                )
                              }
                              if (isFailed) {
                                return (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                                    Error
                                  </span>
                                )
                              }
                              if (isPartial) {
                                return (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                                    Partial
                                  </span>
                                )
                              }
                              if (isComplete) {
                                return (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
                                    Complete
                                  </span>
                                )
                              }
                              return (
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    account.is_active
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {account.is_active ? 'Active' : 'Inactive'}
                                </span>
                              )
                            })()}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => handleToggleAccount(account.id, account.is_active)}
                              disabled={togglingAccountId === account.id}
                              className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                                account.is_active
                                  ? 'text-gray-600 hover:text-red-600 hover:bg-red-50'
                                  : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                              }`}
                              title={account.is_active ? 'Disable account' : 'Enable account'}
                            >
                              {togglingAccountId === account.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : account.is_active ? (
                                <PowerOff className="h-3.5 w-3.5" />
                              ) : (
                                <Power className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add Account Drawer */}
        <AddAccountDrawer
          isOpen={showAddDrawer}
          onClose={() => setShowAddDrawer(false)}
        />
      </div>
    </div>
  )
}

export default BrandScoutPage
